/**
 * A11b — Onboarding Step 3/4: Elegir vertical de negocio
 *
 * Radio card selector with 5 verticals. Celulares leads to
 * the bundle page, everything else to the modules picker.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Scissors,
  Smartphone,
  HardHat,
  Store,
  Settings,
  Info,
} from 'lucide-react';
import { Alert, AlertDescription } from '@shared/ui/shadcn/ui/alert';
import { supabase } from '@shared/lib/supabaseClient';
import OnboardingLayout from '../../components/OnboardingLayout';
import { useOnboarding, VerticalId } from '../../contexts/OnboardingContext';

// ── Verticals definition ─────────────────────────────────────────────────────

interface VerticalDef {
  id: VerticalId;
  icon: React.FC<{ className?: string }>;
  name: string;
  desc: string;
  modules: string[] | null;
  locked: boolean;
  badge: string | null;
  disabled: boolean;
}

const VERTICALS: VerticalDef[] = [
  {
    id: 'tapiceria',
    icon: Scissors,
    name: 'Tapiceria y produccion',
    desc: 'Ordenes de tapizado, materiales por pieza, nomina de operarios y control de taller.',
    modules: ['CRM', 'Cotizaciones', 'Ordenes', 'Inventario', 'Facturacion', 'RRHH'],
    locked: false,
    badge: null,
    disabled: false,
  },
  {
    id: 'celulares',
    icon: Smartphone,
    name: 'Tienda de Celulares',
    desc: 'Venta a credito con bloqueo MDM automatico. Bundle completo preconfigurado -- no se mezcla con otros modulos.',
    modules: ['POS', 'Inventario', 'Credito Movil', 'Enrollment MDM', 'Portal del cliente', 'Reportes'],
    locked: true,
    badge: 'Bundle especializado',
    disabled: false,
  },
  {
    id: 'construccion',
    icon: HardHat,
    name: 'Construccion y materiales',
    desc: 'Proyectos, cotizaciones por obra, control de materiales en campo y avances.',
    modules: ['CRM', 'Cotizaciones', 'Proyectos', 'Inventario', 'Facturacion'],
    locked: false,
    badge: 'Proximamente',
    disabled: true,
  },
  {
    id: 'retail',
    icon: Store,
    name: 'Retail y punto de venta',
    desc: 'POS multi-caja, inventario en tiempo real y fidelizacion de clientes.',
    modules: ['POS', 'Inventario', 'CRM', 'Facturacion'],
    locked: false,
    badge: 'Proximamente',
    disabled: true,
  },
  {
    id: 'otro',
    icon: Settings,
    name: 'Otro tipo de negocio',
    desc: 'Activa solo los modulos que necesitas. Puedes cambiarlos desde Configuracion en cualquier momento.',
    modules: null,
    locked: false,
    badge: null,
    disabled: false,
  },
];

// ── Component ────────────────────────────────────────────────────────────────

const VerticalPage: React.FC = () => {
  const navigate = useNavigate();
  const { state, setVertical } = useOnboarding();
  const { vertical, empresaId } = state;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selected = VERTICALS.find((v) => v.id === vertical);

  const handleNext = async () => {
    if (!vertical) return;

    setError(null);
    setLoading(true);

    try {
      // Save vertical to empresa.tipo_negocio
      if (empresaId) {
        const { error: dbErr } = await (supabase as any)
          .from('empresas')
          .update({ tipo_negocio: vertical })
          .eq('id', empresaId);

        if (dbErr) {
          console.error('[Vertical] DB update error:', dbErr);
          // Non-blocking
        }
      }

      if (vertical === 'celulares') {
        navigate('/onboarding/celulares-bundle');
      } else {
        navigate('/onboarding/modulos');
      }
    } catch (err: any) {
      setError(err?.message || 'Error inesperado.');
    } finally {
      setLoading(false);
    }
  };

  // ── Footer hint text ──
  const footerHint = !vertical
    ? 'Selecciona un tipo de negocio para continuar.'
    : selected?.locked
      ? `Bundle "${selected.name}" -- modulos preconfigurados. Siguiente: wizard de la tienda.`
      : 'Podras elegir los modulos en el siguiente paso.';

  return (
    <OnboardingLayout
      step={3}
      onBack={() => navigate('/onboarding/equipo')}
      onNext={handleNext}
      nextLabel={selected?.locked ? 'Configurar mi tienda' : 'Continuar a modulos'}
      nextDisabled={!vertical}
      loading={loading}
      contentMaxWidth={900}
    >
      <h1 className="text-3xl font-bold tracking-tight">Que tipo de negocio tienes?</h1>
      <p className="text-[15px] text-muted-foreground leading-relaxed max-w-[56ch] mb-5">
        Seleccionamos los modulos correctos para tu industria. Los verticales especializados tienen un bundle cerrado disenado para ese tipo de negocio.
      </p>

      {error && (
        <Alert variant="destructive" className="mb-2">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Vertical cards grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mt-2">
        {VERTICALS.map((v) => {
          const isSelected = vertical === v.id;
          const Icon = v.icon;

          return (
            <div
              key={v.id}
              onClick={() => !v.disabled && setVertical(v.id)}
              className={[
                'border rounded-lg p-4 flex gap-3.5 items-start transition-all',
                v.disabled
                  ? 'opacity-50 cursor-default bg-muted/30'
                  : 'cursor-pointer hover:border-primary/40',
                isSelected
                  ? 'border-primary bg-primary/5'
                  : 'border-border bg-card',
              ].join(' ')}
            >
              {/* Radio indicator */}
              <span
                className={[
                  'w-[18px] h-[18px] rounded-full flex-shrink-0 mt-0.5 flex items-center justify-center border-2',
                  isSelected ? 'border-primary bg-primary' : 'border-border bg-transparent',
                ].join(' ')}
              >
                {isSelected && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
              </span>

              <div className="flex-1 min-w-0">
                {/* Name + badge */}
                <div className="flex items-center gap-2 mb-1">
                  <Icon className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                  <span className="font-semibold text-[14.5px]">{v.name}</span>
                  {v.badge && (
                    <span
                      className={[
                        'font-mono text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded',
                        v.locked
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground border border-border',
                      ].join(' ')}
                    >
                      {v.badge}
                    </span>
                  )}
                </div>

                {/* Description */}
                <p className="text-[12.5px] text-muted-foreground leading-relaxed mb-2">
                  {v.desc}
                </p>

                {/* Module tags */}
                {v.modules && (
                  <div className="flex flex-wrap gap-1">
                    {v.modules.map((m) => (
                      <span
                        key={m}
                        className={[
                          'font-mono text-[10px] font-bold rounded px-1.5 py-0.5 border',
                          isSelected && v.locked
                            ? 'bg-primary/10 border-primary/30 text-primary'
                            : 'bg-muted text-muted-foreground border-border',
                        ].join(' ')}
                      >
                        {m}
                      </span>
                    ))}
                  </div>
                )}

                {/* Bundle info (only when celulares is selected) */}
                {v.locked && isSelected && (
                  <div className="mt-2.5 px-2.5 py-2 bg-blue-50 border border-blue-200 rounded-md text-[12px] text-blue-700 flex gap-2">
                    <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                    <span>Bundle cerrado -- no se combina con otros modulos. Tiene su propio wizard de configuracion.</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer hint */}
      <p className="text-[13px] text-muted-foreground italic mt-3">{footerHint}</p>
    </OnboardingLayout>
  );
};

export default VerticalPage;
